import { CartGroupedSections, CartUpdateEvent } from "@theme/events";
import { morphSection } from "@theme/section-renderer";
import { fetchConfig } from "@theme/utilities";

/**
 * Upgrade a one-time cart line to the first available selling plan (e.g. Recharge).
 */
class CartUpgradeSubscription {
  constructor() {
    document.addEventListener("click", this.#onClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest(".cart-items__upgrade-button") : null;
    if (!button || !(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.#upgrade(button);
  };

  /**
   * @param {HTMLButtonElement} button
   */
  async #upgrade(button) {
    const lineKey = button.dataset.lineKey;
    const variantId = button.dataset.variantId;
    const planId = button.dataset.planId;
    const quantity = Number(button.dataset.quantity || "1") || 1;

    if (!lineKey || !variantId || !planId) {
      console.error("[CartUpgrade] Missing line key, variant id, or selling plan id");
      return;
    }

    button.disabled = true;
    button.classList.add("btn--loading");

    try {
      await fetch(
        FoxTheme.routes.cart_change_url,
        fetchConfig("json", {
          body: JSON.stringify({ id: lineKey, quantity: 0 }),
        })
      );

      const sections = this.#gatherSectionIds();
      const addResponse = await fetch(
        FoxTheme.routes.cart_add_url,
        fetchConfig("json", {
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity,
                selling_plan: Number(planId),
              },
            ],
            sections: sections.join(","),
            sections_url: window.location.pathname,
          }),
        })
      );

      const parsed = await addResponse.json();

      if (parsed.status && parsed.message && !parsed.sections) {
        console.error("[CartUpgrade] Add failed", parsed);
        return;
      }

      if (parsed.sections) {
        for (const sectionId of Object.keys(parsed.sections)) {
          await morphSection(sectionId, parsed.sections[sectionId]);
        }

        const firstSectionId = Object.keys(parsed.sections)[0] || "";
        let itemCount = 0;
        if (firstSectionId) {
          const doc = new DOMParser().parseFromString(parsed.sections[firstSectionId], "text/html");
          const countText = doc.querySelector('[ref="cartItemCount"]')?.textContent;
          itemCount = countText ? parseInt(countText, 10) : 0;
        }

        document.dispatchEvent(
          new CartUpdateEvent(parsed, "cart-upgrade-subscription", {
            itemCount,
            source: "cart-upgrade-subscription",
            sections: parsed.sections,
          })
        );
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error("[CartUpgrade] Upgrade failed", error);
    } finally {
      button.disabled = false;
      button.classList.remove("btn--loading");
    }
  }

  /**
   * @returns {string[]}
   */
  #gatherSectionIds() {
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));

    document.querySelectorAll("cart-items-component[data-section-id]").forEach((el) => {
      const id = el.getAttribute("data-section-id");
      if (id) sections.push(id);
    });

    return [...new Set(sections)];
  }
}

new CartUpgradeSubscription();
